// @flow
import { getJson, postJson, putJson, del } from 'utils/http';
import type { ImageData } from 'api/shared';
import type { ListingApplicationType } from 'api/ats';
import type { ApplicantProfile } from 'api/applicantProfile';

export type SavedSearchIndex = 'ACTIONS' | 'MAIN';

export type SavedSearchUnsaved = {|
  text: string,
  filters: Object,
  location: SearchLocation,
  radius: ?number,
  index: SavedSearchIndex
|};

export type SavedSearch = {
  ...SavedSearchUnsaved,
  id: string,
  created: string
};

export type UserOrgMini = {
  id: string,
  name: string,
  searchApiKey: string,
  addressCountry: string,
  canPostGradProgram: boolean,
  canPostVolops: boolean,
  canBuyCredits: boolean,
  hasGradProgram: boolean,
  gradProgramName: ?string,
  isApproved: boolean,
  hasGradProgram: boolean,
  isAllowedToPost: boolean,
  isFrozen: boolean
};

export type UserGroupMini = {
  id: string,
  name: string,
  searchApiKey: string,
  isApproved: ?boolean,
  isFrozen: boolean
};

export type ABExperiments = Array<{
  id: string,
  created: string,
  bucket: string
}>;

export type CurrentUser = {
  id: ?string,
  email: ?string,
  emailVerified: ?boolean,
  hasPassword: boolean,
  name: ?string,
  firstName: ?string,
  lastName: ?string,
  locale: UserLocale,
  profile?: {
    description: string,
    links: Array<string>,
    image: ?ImageData
  },
  isRecruitable: ?boolean,
  created: string,
  savedSearches: Array<SavedSearch>,
  orgs: Array<UserOrgMini>,
  groups: Array<UserGroupMini>,
  hasEvents: boolean,
  permissions: Array<'user' | 'staff' | 'superStaff' | 'orgAdmin'>,
  searchLocation: SearchLocation,
  location: ?{
    text: string,
    stateCode: ?string,
    countryCode: string,
    latitude: string,
    longitude: string
  },
  timezone: ?string,
  savedListings: Array<{ id: string, type: string }>,
  extraData: {
    staffSearchApiKey?: string
  },
  listingApplications: Array<{ id: string, type: ListingApplicationType }>,
  resumeId: ?string,
  abExperiments: ABExperiments,
  showAcceptCookies: boolean,
  hasNotificationPreferences: boolean,
  showNotifPrefModal: boolean,
  isEU: boolean,
  isUS: boolean,
  idealistSessionId: string,
  canExportData: boolean,
  authProviders: Array<'google_oauth2' | 'facebook'>,
  contactedGradPrograms: Array<string>,
  isLoaded: boolean,
  applicantProfile: ApplicantProfile,
  applicantProfileIsLoaded: boolean,
  applicantProfileIsFetching: boolean,
  hasNotifications: boolean,
  notifications?: Array<Notification>,
  salarySurvey: {
    lastSubmitted: ?string
  }
};

export type ProfileInfo = {
  description: string,
  links: Array<string>,
  image: ?ImageData
};

export type AuthProvider = {
  name: string,
  code: string,
  value: 'google' | 'facebook'
};

export const fetchCurrentUser = (): Promise<CurrentUser> =>
  getJson(`/data/users/me`).then(({ data }) => data);

export const updateSettings = (settings: Object) =>
  putJson('/data/userdashboard/general-settings', settings);

export const updateProfile = (profileInfo: ProfileInfo): Promise<CurrentUser> =>
  putJson('/data/userdashboard/profile', profileInfo).then(({ user }) => user);

export const changePassword = (oldPassword: ?string, password: string) =>
  putJson(`/data/userdashboard/password`, {
    oldPassword,
    password
  });

export const updateMissingInfo = (userInfo: Object): Promise<CurrentUser> =>
  postJson('/data/userdashboard/missing-info', userInfo).then(
    ({ user }) => user
  );

export const canDeleteAccount = () => getJson('/data/users/me/can-delete');
export const deleteAccount = () => del('/data/users/me/delete');

export const unlinkOAuthAccount = (authProvider: string) =>
  del(`/data/userdashboard/linked-accounts/${authProvider}`);

export const saveListing = (id: string, type: string) =>
  postJson(`/data/saved-listings`, {
    id,
    type
  });

export const unsaveListing = (id: string, type: string) =>
  del(`/data/saved-listings/${type}/${id}`);

export const saveSearch = ({
  text,
  filters,
  location,
  index,
  radius
}: SavedSearchUnsaved) =>
  postJson('/data/saved-searches', {
    text,
    filters,
    radius,
    location,
    index
  });

export const unsaveSearch = (searchId: string) =>
  del(`/data/saved-searches/${searchId}`);

export const signUp = (userInfo: Object): Promise<CurrentUser> =>
  postJson('/data/website/signup', userInfo).then(({ user }) => user);

export const logIn = (loginInfo: Object): Promise<CurrentUser> =>
  postJson('/data/website/login', loginInfo).then(({ user }) => user);

export const forgotPassword = (email: string): Promise<void> =>
  postJson('/data/website/forgot-password', { email });

export const resetPassword = ({
  password,
  token
}: {
  password: string,
  token: string
}): Promise<CurrentUser> =>
  postJson(`/data/website/reset-password/${token}`, { password }).then(
    ({ user }) => user
  );

export const submitContactInfo = (
  gradProgramId: string,
  body: {
    email: string,
    firstName: string,
    lastName: string,
    interestedInDegreeProgram?: string,
    expectedEnrollmentDate?: string,
    notes?: string
  }
): Promise<void> =>
  postJson(`/data/website/grad-programs/${gradProgramId}/share-contact`, body);

export const setABExperimentBucket = (
  experimentId: string,
  bucket?: string
): Promise<ABExperiments> =>
  postJson('/data/ab/assignments', {
    id: experimentId,
    bucket
  }).then(({ abExperiments }) => abExperiments);
